import { Injectable } from '@angular/core';
import { Device, Call } from '@twilio/voice-sdk';
import { BehaviorSubject } from 'rxjs';
import { ToasterMessageService } from '../../core/services/topbar-toaster/toaster-message.service';
import { DashboardService } from './dashboard.service';
import { CommonHttpsService } from '../../core/services/https/common-https.service';

@Injectable({
  providedIn: 'root',
})
export class TwilioService {
  private device?: Device;
  public device$ = new BehaviorSubject<Device | undefined>(undefined);
  public activeCall$ = new BehaviorSubject<Call | undefined>(undefined);
  private activeCall?: Call;
  private callData: {
    sid: string;
    duration: number;
  } = { sid: '', duration: 0 };

  constructor(
    public messageService: ToasterMessageService,
    private dashboardService: DashboardService,
    public commonhttpService: CommonHttpsService,
  ) {}

  setActiveCall(call: Call | undefined) {
    this.activeCall = call;
    this.activeCall$.next(call);
  }

  getActiveCall() {
    return this.activeCall;
  }

  getDevice() {
    return this.device;
  }

  getCallData() {
    return this.callData;
  }

  setCallData(sid: string, duration: number) {
    this.callData.sid = sid;
    this.callData.duration = duration;
  }

  sendDigit(digit: string) {
    if (this.activeCall) {
      console.log('Sending digit via TwilioService:', digit);
      this.activeCall.sendDigits(digit);
    } else {
      console.warn('No active call to send digit');
    }
  }

  toggleMute(mute: boolean) {
    if (this.activeCall) {
      console.log('Toggling mute state:', mute);
      this.activeCall.mute(mute);
    } else {
      console.warn('No active call to mute/unmute');
    }
  }

  initialize(token: any) {
    try {
      console.log('Creating Twilio Device...');
      const deviceOptions = {
        allowIncomingWhileBusy: true,
      };
      this.device = new Device(token, deviceOptions);
      // publish device so consumers can attach listeners when available
      this.device$.next(this.device);

      this.device.on('registering', () => {
        console.log('ℹ️ Registering Twilio Device...');
      });

      this.device.register();

      console.log(this.device);

      this.device.on('registered', () => {
        console.log('✅ Twilio Device registered');
        localStorage.setItem('deviceRegistered', 'true');
        // this.messageService.successMessage('Twilio Device registered', true);
      });

      this.device.on('incoming', (call: Call) => {
        console.log('📞 Incoming call:', call);
        // this.messageService.infoMessage('Incoming call', true);
      });

      this.device.on('error', (error: any) => {
        console.error('❌ Twilio Device error:', error);
        localStorage.setItem('deviceRegistered', 'false');
        // this.messageService.errroMessage('Twilio Device error', true);
      });

      // this.device.on('incoming', (call) => {
      //   console.log('📞 Incoming call');
      //   call.accept();
      // });

      this.device.on('ready', () => {
        console.log('✅ Twilio Device ready for calls');
        localStorage.setItem('deviceRegistered', 'true');
        // this.messageService.successMessage(
        //   '✅ Twilio Device ready for calls',
        //   true,
        // );
      });
    } catch (error) {
      console.error('🚫 Failed to initialize Twilio Device:', error);
      localStorage.setItem('deviceRegistered', 'false');
      this.device$.next(undefined);
      // this.messageService.errroMessage(
      //   '🚫 Failed to initialize Twilio Device',
      //   true,
      // );
    }
  }

  async makeCall(
    params: Record<string, string> = {},
    patientId?: any,
    getConfID?: boolean,
  ): Promise<Call | undefined> {
    if (!this.device) {
      console.error('⚠️ Twilio Device is not initialized');
      return undefined;
    }

    try {
      const call: Call = await this.device.connect({ params });

      call.on('ringing', () => {
        console.log('Phone is ringing...');
      });

      call.on('accept', (func: any) => {
        console.log('✅ Call accepted', func);

        localStorage.setItem('callActive', 'true');
        // keep a reference to the active call so other components can access it
        this.activeCall = call;
        this.activeCall$.next(call);
        if (patientId) {
          // if (!getConfID) {
          //   return;
          // }
          // setTimeout(() => {
          //   this.dashboardService
          //     .GetConferenceId(patientId)
          //     .subscribe((data: any) => {
          //       if (typeof (this as any).onConferenceIdFetched === 'function') {
          //         (this as any).onConferenceIdFetched(data.conferenceId);
          //       }
          //     });
          // }, 3400);
        } else {
          console.warn('No patientId provided for GetConferenceId');
        }
        //https://twilioapidev.carevance.solutions/api/GetConferenceId?patientId=6666
      });

      call.on('disconnect', () => {
        localStorage.setItem('callActive', 'false');
        // clear activeCall reference
        this.activeCall = undefined;
        this.activeCall$.next(undefined);
        this.device?.disconnectAll();
      });

      call.on('error', (err: any) => {
        localStorage.setItem('callActive', 'false');
        console.error('❗ Call error:', err);
        this.activeCall = undefined;
        this.activeCall$.next(undefined);
      });

      call.on('warning', (warning: any) => {
        console.warn('⚠️ Call warning:', warning);
      });

      return call;
    } catch (error) {
      localStorage.setItem('callActive', 'false');
      console.error('🚫 Failed to make call:', error);
      return undefined;
    }
  }

  disconnectCall() {
    this.device?.disconnectAll();
    localStorage.setItem('callActive', 'false');
    this.activeCall = undefined;
    this.activeCall$.next(undefined);
  }

  destroy() {
    this.device?.destroy();
    localStorage.setItem('callActive', 'false');
    this.device$.next(undefined);
    this.activeCall = undefined;
    this.activeCall$.next(undefined);
  }
}
